<script setup>
import { computed } from 'vue'
import { Handle, Position } from '@vue-flow/core'

// eslint-disable-next-line no-undef
const props = defineProps({
  id: { type: String, required: true },
  data: { type: Object, required: true },
  selected: { type: Boolean, default: false },
  dragging: { type: Boolean, default: false },
})

const kindLabel = computed(() => {
  const kind = props.data?.kind || 'message'
  return {
    start: 'Start',
    message: 'Message',
    voice: 'Voice',
    collect: 'Collect info',
    decision: 'Decision',
    knowledge: 'Knowledge',
    booking: 'Booking',
    payment: 'Payment',
    transfer: 'Transfer',
    contact: 'SMS / Email',
    webhook: 'Webhook',
    end: 'End',
  }[kind] || 'Block'
})

const variantClass = computed(() => `workflow-node--${props.data?.kind || 'message'}`)

const badges = computed(() => {
  const items = []
  if (props.data?.config?.language) items.push(props.data.config.language)
  if (props.data?.config?.providerVoiceId) items.push(props.data.config.providerVoiceId)
  if (props.data?.config?.service && props.data.kind === 'booking') items.push(props.data.config.service)
  if (props.data?.config?.mode && props.data.kind === 'payment') items.push(props.data.config.mode)
  if (Array.isArray(props.data?.badges)) items.push(...props.data.badges)
  return items.slice(0, 4)
})

const handles = computed(() => {
  const kind = props.data?.kind
  if (kind === 'start') {
    return [{ id: 'out', type: 'source', position: Position.Bottom }]
  }
  if (kind === 'end') {
    return [{ id: 'in', type: 'target', position: Position.Top }]
  }
  if (kind === 'decision') {
    return [
      { id: 'in', type: 'target', position: Position.Top },
      { id: 'yes', type: 'source', position: Position.Left },
      { id: 'no', type: 'source', position: Position.Right },
      { id: 'fallback', type: 'source', position: Position.Bottom },
    ]
  }

  return [
    { id: 'in', type: 'target', position: Position.Top },
    { id: 'out', type: 'source', position: Position.Bottom },
  ]
})
</script>

<template>
  <div class="workflow-node" :class="[variantClass, { 'is-selected': selected, 'is-dragging': dragging }]">
    <div class="workflow-node__handle-layer">
      <Handle
        v-for="handle in handles"
        :key="handle.id"
        :id="handle.id"
        :type="handle.type"
        :position="handle.position"
        class="workflow-node__handle"
      />
    </div>

    <div class="workflow-node__header">
      <div class="workflow-node__icon">{{ data?.icon || '•' }}</div>
      <div class="workflow-node__titles">
        <div class="workflow-node__kind">{{ kindLabel }}</div>
        <div class="workflow-node__title">{{ data?.title || 'Untitled block' }}</div>
      </div>
    </div>

    <div class="workflow-node__description">
      {{ data?.description || 'No description provided.' }}
    </div>

    <div v-if="data?.summary" class="workflow-node__summary">
      {{ data.summary }}
    </div>

    <div v-if="badges.length" class="workflow-node__badges">
      <span v-for="badge in badges" :key="badge" class="workflow-node__badge">{{ badge }}</span>
    </div>

    <div v-if="Array.isArray(data?.config?.branches) && data.config.branches.length" class="workflow-node__branches">
      <span
        v-for="branch in data.config.branches"
        :key="branch.id || branch.label"
        class="workflow-node__branch"
      >
        {{ branch.label }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.workflow-node {
  position: relative;
  min-width: 240px;
  max-width: 320px;
  padding: 16px 16px 14px;
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
  color: #0f172a;
}

.workflow-node.is-selected {
  border-color: rgba(59, 130, 246, 0.75);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12), 0 24px 45px rgba(15, 23, 42, 0.12);
}

.workflow-node__header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.workflow-node__icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #0f172a, #334155);
  flex: 0 0 auto;
}

.workflow-node--start .workflow-node__icon,
.workflow-node--end .workflow-node__icon {
  background: linear-gradient(135deg, #0f9d58, #2dd4bf);
}

.workflow-node--decision .workflow-node__icon {
  background: linear-gradient(135deg, #f59e0b, #f97316);
}

.workflow-node--booking .workflow-node__icon,
.workflow-node--knowledge .workflow-node__icon {
  background: linear-gradient(135deg, #14b8a6, #22c55e);
}

.workflow-node--payment .workflow-node__icon {
  background: linear-gradient(135deg, #e11d48, #fb7185);
}

.workflow-node--transfer .workflow-node__icon {
  background: linear-gradient(135deg, #475569, #64748b);
}

.workflow-node--collect .workflow-node__icon,
.workflow-node--voice .workflow-node__icon {
  background: linear-gradient(135deg, #2563eb, #60a5fa);
}

.workflow-node--contact .workflow-node__icon,
.workflow-node--webhook .workflow-node__icon {
  background: linear-gradient(135deg, #4f46e5, #818cf8);
}

.workflow-node__titles {
  min-width: 0;
}

.workflow-node__kind {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.workflow-node__title {
  margin-top: 2px;
  font-size: 15px;
  line-height: 1.2;
  font-weight: 700;
  color: #0f172a;
  word-break: break-word;
}

.workflow-node__description,
.workflow-node__summary {
  margin-top: 10px;
  font-size: 13px;
  line-height: 1.45;
  color: #475569;
  word-break: break-word;
}

.workflow-node__summary {
  padding: 10px 12px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.workflow-node__badges,
.workflow-node__branches {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.workflow-node__badge,
.workflow-node__branch {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 5px 9px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
}

.workflow-node__branch {
  background: #f1f5f9;
  color: #334155;
}

.workflow-node__handle-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.workflow-node__handle {
  width: 12px;
  height: 12px;
  border: 2px solid white;
  background: #2563eb;
  box-shadow: 0 4px 8px rgba(15, 23, 42, 0.2);
  pointer-events: auto;
}
</style>
